import moment from "moment-timezone";
import type { SalonService } from "../../../../types/salon";
import type { EditTransactionLineItemsRequest } from "../../../../types/transactions";
import { ServiceRow, staffServiceFor } from "./bookingTypes";

/**
 * The staff_service_id linking a row's provider to its service, if any: the
 * service's servicing_staff entry for that provider.
 */
export const resolveStaffServiceId = (
  services: SalonService[] | null | undefined,
  row: Pick<ServiceRow, "employeeId" | "salonServiceId">,
): string | undefined =>
  staffServiceFor(
    (services || []).find((s) => s.id === row.salonServiceId),
    row.employeeId,
  )?.id;

/**
 * A row's start as a UTC ISO instant. `date` + `time` are salon wall-clock
 * (a local string, not an API datetime), so they are read in the salon tz.
 */
export const rowStartIso = (
  row: Pick<ServiceRow, "date" | "time">,
  timezone: string,
): string =>
  moment
    .tz(`${row.date} ${row.time}`, "YYYY-MM-DD HH:mm", timezone)
    .toISOString();

/**
 * The row's adjustment answers differ from the ones it was seeded with
 * (order-insensitive). False for a row without seeded answers to compare.
 */
const adjustmentsChanged = (row: ServiceRow): boolean => {
  if (!row.sourceAdjustmentOptionIds) return false;
  const now = [...(row.adjustmentOptionIds || [])].sort();
  const seeded = [...row.sourceAdjustmentOptionIds].sort();
  return now.length !== seeded.length || now.some((id, i) => id !== seeded[i]);
};

/**
 * Reschedule ("edit") write: PUT the source appointment's existing booked
 * services in place (time / staff / durations / price). `base_duration_mins`
 * is the row's unadjusted base: the appointment's adjustment sub line items
 * carry their own minutes. `service_adjustment_form_option_ids` is sent only
 * for a row whose answers changed (the backend then replaces that line
 * item's sub line items); otherwise they are left as they are. Rows must carry their
 * source booked-service + line-item uuids (seedFromTransaction "edit").
 * Shared by the booking drawer and the mobile reschedule section. The
 * response may carry `split_transaction_uuids` (cross-day move) that the
 * caller refetches along with the source.
 */
export const buildEditLineItemsPayload = (
  transactionId: string,
  rows: ServiceRow[],
  timezone: string,
  staffServiceIdFor: (row: ServiceRow) => string | undefined,
): EditTransactionLineItemsRequest => ({
  transaction_id: transactionId,
  line_items: {
    booked_services: rows
      .filter((r) => r.sourceBookedServiceId && r.sourceLineItemId)
      .map((r) => ({
        id: r.sourceBookedServiceId as string,
        line_item_id: r.sourceLineItemId as string,
        staff_service_id: staffServiceIdFor(r),
        start_dt: rowStartIso(r, timezone),
        base_duration_mins: r.baseDuration,
        processing_time_mins: r.processingTime,
        finishing_time_mins: r.finishingTime,
        buffer_time_mins: r.bufferTime,
        unit_price: r.price,
        ...(adjustmentsChanged(r)
          ? { service_adjustment_form_option_ids: r.adjustmentOptionIds || [] }
          : {}),
      })),
  },
});
